import React, { useCallback, useMemo } from "react";
import { FiCalendar } from "react-icons/fi";
import StatusBadge from "../common/StatusBadge";
import RoomChip from "../common/RoomChip";
import AsyncSection from "../common/AsyncSection";
import DataTable from "../common/DataTable";
import useAdminResource from "../../hooks/useAdminResource";
import adminUserService from "../../services/adminUserService";
import { formatWhen, formatDateTime } from "./scheduleFormat";

/**
 * Device detail content — shared by the Detail modal and the
 * /devices/:deviceId route.
 *
 * `onOpenSchedule` / `onOpenRoom` are injected so the modal can hand off to
 * another modal while the page can navigate; the body itself does not care.
 */
function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
        {label}
      </p>
      <div className="mt-1 text-sm text-slate-800">{children}</div>
    </div>
  );
}

export function DeviceDetailBadges({ device }) {
  return (
    <>
      <StatusBadge kind="deviceStatus" value={device.status} size="md" />
      <StatusBadge kind="powerState" value={device.deviceOn} unknownLabel="—" size="md" />
      <StatusBadge kind="isAdded" value={device.isAdded !== false} size="md" />
    </>
  );
}

export default function DeviceDetailBody({
  device,
  userId,
  onOpenSchedule,
  onOpenRoom,
}) {
  // ⚠️ VENDOR id — Schedule.actions[].deviceId stores this string, never _id.
  const vendorId = device?.deviceId;

  const fetcher = useCallback(
    () =>
      vendorId
        ? adminUserService.fetchUserSchedules(userId, { deviceId: vendorId, limit: 50 })
        : Promise.resolve({ items: [] }),
    [userId, vendorId]
  );

  const { data, state, error, refetch } = useAdminResource(fetcher, [userId, vendorId], {
    enabled: Boolean(vendorId),
  });

  const schedules = data || [];

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Schedule",
        cell: ({ row }) => (
          <span className="font-semibold text-slate-800">{row.original.name}</span>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge kind="scheduleStatus" value={row.original.status} />,
      },
      {
        id: "when",
        header: "When",
        cell: ({ row }) => (
          <span className="text-sm text-slate-600">{formatWhen(row.original)}</span>
        ),
      },
      {
        id: "action",
        header: "This device",
        cell: ({ row }) => {
          const action = (row.original.actions || []).find((a) => a.deviceId === vendorId);
          return <StatusBadge kind="powerState" value={action?.deviceOn} unknownLabel="—" />;
        },
      },
      {
        id: "next",
        header: "Next run",
        cell: ({ row }) => (
          <span className="text-sm text-slate-600">
            {formatDateTime(row.original.nextRunAt)}
          </span>
        ),
      },
    ],
    [vendorId]
  );

  if (!device) {
    return (
      <p className="py-8 text-center text-sm text-slate-500">
        This device is no longer in the user's account.
      </p>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
        <Field label="Brand">
          <span className="capitalize">{device.brand || "—"}</span>
        </Field>
        <Field label="Room">
          <RoomChip
            room={device.roomId}
            onClick={device.roomId?._id ? () => onOpenRoom?.(device.roomId) : undefined}
          />
        </Field>
        <Field label="Smart Restore">
          {device.smartRestore?.enabled ? (
            <span className="font-medium text-indigo-600">
              ✨ On
              {device.smartRestore.expectedState != null &&
                ` · expects ${device.smartRestore.expectedState ? "ON" : "OFF"}`}
            </span>
          ) : (
            <span className="text-slate-400">Off</span>
          )}
        </Field>
        <Field label="In schedules">
          {device.activeScheduleCount ?? 0} active
          {device.totalScheduleCount != null &&
            device.totalScheduleCount !== device.activeScheduleCount && (
              <span className="text-slate-400"> · {device.totalScheduleCount} total</span>
            )}
        </Field>
      </div>

      {device.smartRestore?.sourceScheduleId && (
        <p className="mt-4 text-sm text-slate-600">
          Smart Restore source:{" "}
          <button
            type="button"
            onClick={() => onOpenSchedule?.({ scheduleId: device.smartRestore.sourceScheduleId })}
            className="font-medium text-indigo-600 hover:underline"
          >
            view schedule
          </button>
        </p>
      )}

      <h3 className="mb-3 mt-7 text-xs font-bold uppercase tracking-wide text-slate-500">
        Schedules driving this device
      </h3>

      <AsyncSection
        state={state}
        error={error}
        onRetry={() => refetch()}
        isEmpty={schedules.length === 0}
        loadingMessage="Loading schedules…"
        emptyIcon={FiCalendar}
        emptyTitle="Not in any schedule"
        emptyMessage="No schedule references this device."
        compact
      >
        <DataTable
          columns={columns}
          data={schedules}
          state={state}
          error={error}
          onRetry={() => refetch()}
          getRowId={(row) => String(row.scheduleId)}
          onRowClick={(row) => onOpenSchedule?.(row)}
        />
      </AsyncSection>
    </div>
  );
}
